import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Typography,
  Button,
  Box,
  Paper,
  Rating,
  Divider,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  Skeleton,
  ImageList,
  ImageListItem,
  Snackbar,
  Alert,
  Breadcrumbs,
  Link,
  CardMedia,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  CircularProgress,
  Modal
} from '@mui/material';
import {
  ShoppingCart as ShoppingCartIcon,
  FavoriteBorder as FavoriteBorderIcon,
  LocalShipping as LocalShippingIcon,
  Security as SecurityIcon,
  Loop as LoopIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Share,
  Verified,
  WhatsApp,
  ExpandMore,
  LocalShipping,
  Verified as VerifiedIcon,
  WhatsApp as WhatsAppIcon,
  ExpandMore as ExpandMoreIcon,
  LocalShippingOutlined,
  Close,
  ArrowBack,
  ArrowForward
} from '@mui/icons-material';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import SEO from '../components/SEO';

function ProductDetail() {
  const { id } = useParams();
  const { addToCart } = useCart();
  const { currentUser } = useAuth();
  const [product, setProduct] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mainImage, setMainImage] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const navigate = useNavigate();
  const [openModal, setOpenModal] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const docRef = doc(db, 'products', id);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          setProduct({ id: docSnap.id, ...docSnap.data() });
          setMainImage(docSnap.data().images?.[0] || '');
        } else {
          setError('Ürün bulunamadı');
        }
      } catch (error) {
        console.error('Ürün yüklenirken hata:', error);
        setError('Ürün yüklenirken bir hata oluştu');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  const handleAddToCart = () => {
    if (!selectedSize) {
      alert('Lütfen bir beden seçin');
      return;
    }
    if (!selectedColor) {
      alert('Lütfen bir renk seçin');
      return;
    }

    addToCart({
      ...product,
      quantity,
      selectedSize,
      selectedColor
    });
    
    setOpenSnackbar(true);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
  };

  const handleShare = async () => {
    const currentURL = window.location.href;
    
    try {
      if (navigator.share) {
        // Mobil cihazlarda native paylaşım
        await navigator.share({
          title: product?.title,
          text: `${product?.title} - Kayla`,
          url: currentURL
        });
      } else {
        // Desktop'ta link kopyalama
        await navigator.clipboard.writeText(currentURL);
        setOpenSnackbar(true);
      }
    } catch (error) {
      console.error('Paylaşım hatası:', error);
    }
  };

  const productJsonLd = {
    "@context": "https://schema.org/",
    "@type": "Product",
    "name": product?.title,
    "image": product?.images,
    "description": product?.description,
    "offers": {
      "@type": "Offer",
      "url": window.location.href,
      "priceCurrency": "TRY",
      "price": product?.price,
      "availability": product?.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock"
    }
  };

  const handleOpenModal = (index) => {
    setCurrentImageIndex(index);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? product?.images?.length - 1 : prev - 1
    );
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => 
      prev === product?.images?.length - 1 ? 0 : prev + 1
    );
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Skeleton variant="rectangular" height={400} />
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={1}>
                {[1, 2, 3, 4].map((item) => (
                  <Grid item xs={3} key={item}>
                    <Skeleton variant="rectangular" height={100} />
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Skeleton variant="text" height={60} />
            <Skeleton variant="text" height={30} width="60%" />
            <Skeleton variant="text" height={25} width="40%" />
            <Box sx={{ my: 3 }}>
              <Skeleton variant="rectangular" height={100} />
            </Box>
            <Skeleton variant="rectangular" height={50} />
          </Grid>
        </Grid>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h5">{error}</Typography>
        </Paper>
      </Container>
    );
  }

  if (!product) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h5">Ürün bulunamadı</Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <>
      <SEO 
        title={product?.title}
        description={`${product?.title} - ${product?.description?.substring(0, 150)}...`}
        image={product?.images?.[0]}
      />
      <script type="application/ld+json">
        {JSON.stringify(productJsonLd)}
      </script>
      <Container 
        maxWidth="lg" 
        sx={{ 
          mt: { xs: 12, sm: 16 },
          mb: 6,
          minHeight: '100vh',
        }}
      >
        <Grid container spacing={4}>
          {/* Sol Taraf - Ürün Görselleri */}
          <Grid item xs={12} md={6}>
            <Box sx={{ position: 'relative' }}>
              {/* Ana Görsel */}
              <Box
                onClick={() => handleOpenModal(0)}
                sx={{
                  width: '100%',
                  height: '600px',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  mb: 2,
                  position: 'relative',
                  cursor: 'zoom-in',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: '#f5f5f5'
                }}
              >
                <img
                  src={selectedImage || product?.images?.[0]}
                  alt={product?.title}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    transition: 'transform 0.4s ease'
                  }}
                />
                {product?.discount && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      bgcolor: 'error.main',
                      color: 'white',
                      px: 2,
                      py: 0.5,
                      borderRadius: '20px',
                      fontSize: '1rem',
                      fontWeight: 600,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                      zIndex: 1
                    }}
                  >
                    %{product.discount} İndirim
                  </Box>
                )}
              </Box>

              {/* Küçük Görseller */}
              <Box sx={{ 
                display: 'flex', 
                gap: 2,
                justifyContent: 'center',
                flexWrap: 'wrap'
              }}>
                {product?.images?.map((image, index) => (
                  <Box
                    key={index}
                    onClick={() => setSelectedImage(image)}
                    sx={{
                      width: '100px',
                      height: '100px',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      border: selectedImage === image ? '2px solid' : '2px solid transparent',
                      borderColor: 'primary.main',
                      bgcolor: '#f5f5f5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <img
                      src={image}
                      alt={`${product?.title} - ${index + 1}`}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain'
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          </Grid>

          {/* Sağ Taraf - Ürün Detayları */}
          <Grid item xs={12} md={6}>
            <Box sx={{ 
              position: 'sticky', 
              top: 80,
              bgcolor: 'background.paper',
              p: 3,
              borderRadius: '16px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
            }}>
              <Typography
                variant="h4"
                component="h1"
                sx={{ 
                  fontWeight: 600,
                  mb: 2,
                  color: 'text.primary'
                }}
              >
                {product?.title}
              </Typography>

              <Typography
                variant="h5"
                sx={{ 
                  fontWeight: 600,
                  mb: 3,
                  color: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2
                }}
              >
                {product?.price?.toLocaleString('tr-TR')} TL
                {product?.oldPrice && (
                  <Typography
                    component="span"
                    sx={{
                      textDecoration: 'line-through',
                      color: 'text.secondary',
                      fontSize: '1rem'
                    }}
                  >
                    {product?.oldPrice?.toLocaleString('tr-TR')} TL
                  </Typography>
                )}
              </Typography>

              <Typography
                sx={{ 
                  mb: 4,
                  color: 'text.secondary',
                  lineHeight: 1.8
                }}
              >
                {product?.description}
              </Typography>

              {/* Beden Seçimi */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
                  Beden
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {product?.sizes?.map((size) => (
                    <Button
                      key={size}
                      variant={selectedSize === size ? "contained" : "outlined"}
                      onClick={() => setSelectedSize(size)}
                      sx={{
                        minWidth: '48px',
                        height: '48px',
                        borderRadius: '8px'
                      }}
                    >
                      {size}
                    </Button>
                  ))}
                </Box>
              </Box>

              {/* Renk Seçimi */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
                  Renk
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {product?.colors?.map((color) => (
                    <Button
                      key={color}
                      variant={selectedColor === color ? "contained" : "outlined"}
                      onClick={() => setSelectedColor(color)}
                      sx={{
                        minWidth: '48px',
                        height: '48px',
                        borderRadius: '8px'
                      }}
                    >
                      {color}
                    </Button>
                  ))}
                </Box>
              </Box>

              {/* Sepete Ekle Butonu */}
              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={handleAddToCart}
                disabled={!selectedSize}
                sx={{
                  height: '56px',
                  borderRadius: '12px',
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 16px rgba(0,0,0,0.2)'
                  }
                }}
              >
                Sepete Ekle
              </Button>

              {/* Kargo Bilgisi */}
              <Box sx={{ 
                mt: 4, 
                p: 2, 
                bgcolor: 'grey.50',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: 2
              }}>
                <LocalShippingOutlined color="primary" />
                <Typography variant="body2" color="text.secondary">
                  150 TL ve üzeri alışverişlerde kargo ücretsiz!
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Fotoğraf Modal'ı */}
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'rgba(0, 0, 0, 0.9)'
        }}
      >
        <Box sx={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }}>
          {/* Kapat Butonu */}
          <IconButton
            onClick={handleCloseModal}
            sx={{
              position: 'absolute',
              right: -40,
              top: -40,
              color: 'white',
              bgcolor: 'rgba(255,255,255,0.1)',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.2)'
              }
            }}
          >
            <Close />
          </IconButton>

          {/* Önceki/Sonraki Butonları */}
          <IconButton
            onClick={handlePrevImage}
            sx={{
              position: 'absolute',
              left: -50,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'white',
              bgcolor: 'rgba(255,255,255,0.1)',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.2)'
              }
            }}
          >
            <ArrowBack />
          </IconButton>

          <IconButton
            onClick={handleNextImage}
            sx={{
              position: 'absolute',
              right: -50,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'white',
              bgcolor: 'rgba(255,255,255,0.1)',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.2)'
              }
            }}
          >
            <ArrowForward />
          </IconButton>

          {/* Büyük Fotoğraf */}
          <img
            src={product?.images?.[currentImageIndex]}
            alt={product?.title}
            style={{
              maxWidth: '90vw',
              maxHeight: '90vh',
              objectFit: 'contain',
              display: 'block',
              margin: '0 auto'
            }}
          />
        </Box>
      </Modal>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity="success" 
          variant="filled"
          sx={{ width: '100%' }}
        >
          Ürün başarıyla sepete eklendi!
        </Alert>
      </Snackbar>
    </>
  );
}

export default ProductDetail; 